import { useMemo, useState } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import { Box, Chip, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';

import type { IntegrationProvider } from '@/types';

import type { ContentfulConnectorEntry } from '../api/contentfulConnectors';
import { useGetIntegrationTypesQuery } from '../api/environmentsApi';
import { useContentfulConnectors } from '../hooks/useContentfulConnectors';
import { getConnectorIcon } from '../utils/connectorIcons';

type ConnectorFilter = 'reader' | 'writer';
type SortOption = 'Alphabetic' | 'Relevance';

interface ConnectorSelectorProps {
  filter: ConnectorFilter;
  onSelect: (provider: IntegrationProvider) => void;
  existingCredentialTypes?: string[];
  showSkip?: boolean;
  onSkip?: () => void;
}

/**
 * Connector selection grid matching legacy dataProtect ConnectorBar.js:
 * - Uses Contentful CMS for connector list (displayOnAppEnvironments, forIntegrationOnly)
 * - Intersects with API providers when adding (newConnector=true)
 * - Filters by reader/writer based on readerName/writerName
 * - Sort by Alphabetic or Relevance
 * - Shows connector icon (Contentful logo or fallback), label, and category chips
 * - Falls back to API-only when Contentful fails
 */
export const ConnectorSelector = ({
  filter,
  onSelect,
  existingCredentialTypes = [],
  showSkip = false,
  onSkip,
}: ConnectorSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('Alphabetic');

  const {
    connectors: contentfulConnectors,
    isLoading: contentfulLoading,
    error: contentfulError,
  } = useContentfulConnectors();
  const { data: providers = [] } = useGetIntegrationTypesQuery();

  // remainingProviders = API providers filtered by reader/writer, excluding LingkWebSocket
  // Matches ConnectorBar.js 155-174 exactly: when a LingkWebSocket credential exists,
  // the old UI replaces the reader/writer-filtered list with ALL providers minus LingkWebSocket
  const remainingProviders = useMemo(() => {
    const hasWebSocket = existingCredentialTypes.includes('LingkWebSocket');
    if (hasWebSocket) {
      return providers.filter((p) => p.type !== 'LingkWebSocket');
    }
    return filter === 'reader'
      ? providers.filter((p) => p.readerName === 'Reader')
      : providers.filter((p) => p.writerName === 'Writer');
  }, [filter, providers, existingCredentialTypes]);

  // Use Contentful when available; fallback to API-only when Contentful fails
  const useContentful = !contentfulError;

  // connectorItems from Contentful (intersected with API) or fallback to API providers
  const { connectorItems, displayMode } = useMemo(() => {
    if (useContentful) {
      const searched = contentfulConnectors.filter((entry) => {
        const matchesSearch =
          !searchTerm || entry.fields.title.toLowerCase().includes(searchTerm.toLowerCase());
        const providerExistsInApi = remainingProviders.some(
          (p) => p.type === entry.fields.providerType
        );
        return matchesSearch && providerExistsInApi;
      });
      const sorted = [...searched];
      if (sortBy === 'Alphabetic') {
        sorted.sort((a, b) =>
          a.fields.title.toUpperCase().localeCompare(b.fields.title.toUpperCase())
        );
      } else {
        sorted.sort((a, b) => {
          const relA = a.fields.relevance ?? 10;
          const relB = b.fields.relevance ?? 10;
          if (relA < relB) return -1;
          if (relA > relB) return 1;
          return 0;
        });
      }
      return { connectorItems: sorted, displayMode: 'contentful' as const };
    }
    // Fallback: API-only (original behavior)
    const searched = searchTerm
      ? remainingProviders.filter((p) =>
          (p.label || p.type).toLowerCase().includes(searchTerm.toLowerCase())
        )
      : remainingProviders;
    const sorted = [...searched].sort((a, b) =>
      (a.label || a.type).localeCompare(b.label || b.type)
    );
    return { connectorItems: sorted, displayMode: 'api' as const };
  }, [useContentful, contentfulConnectors, remainingProviders, searchTerm, sortBy]);

  const handleSelectContentful = (entry: ContentfulConnectorEntry): void => {
    const provider = remainingProviders.find((p) => p.type === entry.fields.providerType);
    if (provider) {
      onSelect(provider);
    }
  };

  const handleSelectProvider = (provider: IntegrationProvider): void => {
    onSelect(provider);
  };

  const getLogoUrl = (entry: ContentfulConnectorEntry): string | undefined =>
    entry.fields.logo?.fields?.file?.url;

  if (contentfulLoading && contentfulConnectors.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Search bar - matches ConnectorBar.js placeholder */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search Connectors"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        autoFocus
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: '#999' }} />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': { fontSize: '13px' },
        }}
      />

      {/* Count + Sort + Skip button - matches ConnectorBar.js "41 sources" and Sort by Alphabetic/Relevance */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
            {connectorItems.length} {filter === 'reader' ? 'sources' : 'destinations'}
          </Typography>
          {showSkip && onSkip && (
            <Chip
              label="Skip"
              size="small"
              onClick={onSkip}
              sx={{
                fontSize: '11px',
                height: 24,
                cursor: 'pointer',
                backgroundColor: '#f0f0f0',
                '&:hover': { backgroundColor: '#e0e0e0' },
              }}
            />
          )}
        </Box>
        {displayMode === 'contentful' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
              Sort by <b>{sortBy}</b>
            </Typography>
            <Chip
              label={sortBy === 'Alphabetic' ? 'Relevance' : 'Alphabetic'}
              size="small"
              onClick={() => setSortBy(sortBy === 'Alphabetic' ? 'Relevance' : 'Alphabetic')}
              sx={{
                fontSize: '11px',
                height: 24,
                cursor: 'pointer',
                backgroundColor: '#dcdfdf',
                '&:hover': { backgroundColor: '#c8cdcd' },
              }}
            />
          </Box>
        )}
      </Box>

      {contentfulError && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '12px' }}>
          Using API connector list (Contentful unavailable).
        </Typography>
      )}

      {/* Connector grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 1.5,
          maxHeight: 'calc(100vh - 230px)',
          overflowY: 'auto',
          pr: 1,
        }}
      >
        {displayMode === 'contentful'
          ? (connectorItems as ContentfulConnectorEntry[]).map((entry) => {
              const provider = remainingProviders.find((p) => p.type === entry.fields.providerType);
              const logoUrl = getLogoUrl(entry);
              return (
                <Box
                  key={entry.sys.id}
                  onClick={() => handleSelectContentful(entry)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#00853E',
                      backgroundColor: '#f5faf7',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    {logoUrl ? (
                      <Box
                        component="img"
                        src={logoUrl}
                        alt=""
                        sx={{ width: 24, height: 24, objectFit: 'contain' }}
                      />
                    ) : (
                      provider && getConnectorIcon(provider.label || provider.type, provider.type)
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        wordBreak: 'break-word',
                        lineHeight: 1.3,
                      }}
                    >
                      {entry.fields.title}
                    </Typography>
                    {entry.fields.categories && entry.fields.categories.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {entry.fields.categories?.map((cat, idx) => (
                          <Chip
                            key={idx}
                            label={cat.fields.title}
                            size="small"
                            sx={{
                              fontSize: '10px',
                              height: 18,
                              backgroundColor: '#f5f5f5',
                              color: '#666',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })
          : (connectorItems as IntegrationProvider[]).map((provider) => (
              <Box
                key={provider.type}
                onClick={() => handleSelectProvider(provider)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: '#00853E',
                    backgroundColor: '#f5faf7',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  },
                }}
              >
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                  {getConnectorIcon(provider.label || provider.type, provider.type)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      fontWeight: 500,
                      wordBreak: 'break-word',
                      lineHeight: 1.3,
                    }}
                  >
                    {provider.label || provider.type}
                  </Typography>
                </Box>
              </Box>
            ))}
      </Box>

      {connectorItems.length === 0 && (
        <Typography variant="body2" sx={{ mt: 2, color: '#999', fontSize: '13px' }}>
          No connectors found{searchTerm ? ` matching "${searchTerm}"` : ''}.
        </Typography>
      )}
    </Box>
  );
};
